import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardDetailsSpacer,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardPrice,
    EventCardPromoCode,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardShareAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    getEventCardLinkProps,
    getFormattedSalesStatus,
    getShareActionProps,
    HorizontalEventCard,
    HorizontalEventCardColumn,
    HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import React from 'react';

import { useGetPromotedLabelPosition } from '../../DiscoverEventCardExperiments';
import { DiscoverEventCardProps } from '../../types';
import '../DiscoverHorizontalEventCard/DiscoverHorizontalEventCard.scss';

export const DiscoverHorizontalEventCard: React.FunctionComponent<
    DiscoverEventCardProps
> = (props) => {
    const {
        event,
        locale,
        isRaised,
        statsigLocationString,
        affCode = 'ebdssbdestsearch',
        moreActions,
        isExpansionsLoading,
    } = props;
    const { urgencySignals } = event;

    const linkProps = getEventCardLinkProps({
        affCode: affCode,
        url: event?.url,
        isTargetBlank: true,
        isNoFollow: event.ticketsBy !== 'Eventbrite',
        onClick: (e) => props.onClick?.(e),
        label: gettext('View %(eventName)s', { eventName: event.name }),
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    const formattedPriceString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    //Only show active Sales Status (sold out etc)
    //For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = getFormattedSalesStatus(event);

    const shouldShowUrgencySignals = !!urgencySignals && !formattedSalesStatus;

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        event,
    };

    const promotedLabel = (
        <EventCardPromoted
            isPromoted={event.isPromoted}
            tokens={{ '--PromotedPaddingBottom': '4px' }}
        />
    );
    const {
        promotedLabelAsHeader,
        promotedLabelAsSubtitle,
        promotedLabelAsBottom,
    } = useGetPromotedLabelPosition(promotedLabel);

    return (
        <section className="discover-horizontal-event-card">
            <HorizontalEventCard isRaised={isRaised} actionVisibility="hidden">
                <EventCardTracking {...eventCardTrackingProps}>
                    <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                        {formattedSalesStatus}
                    </EventCardBadge>
                    <HorizontalEventCardColumn
                        tokens={{
                            '--HorizontalCardColumnMarginRight': '24px',
                        }}
                    >
                        {shouldShowUrgencySignals && (
                            <EventCardUrgencySignals
                                className="discover-horizontal-event-card__urgency-signals--left"
                                urgencySignals={urgencySignals}
                            />
                        )}
                        <EventCardLink {...linkProps}>
                            <EventCardImage
                                name={event.name}
                                loading={props.isLazyImage ? 'lazy' : 'eager'}
                                tokens={{
                                    ...HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
                                    '--image-background-color': event.edgeColor,
                                }}
                                src={event.imageUrl}
                            />
                        </EventCardLink>
                    </HorizontalEventCardColumn>
                    <EventCardDetails
                        space="xs"
                        tokens={{
                            '--EventCardDetailsPadding': '0 16px 0 0',
                            '--EventCardDetailsFlexGrow': '2',
                        }}
                    >
                        {promotedLabelAsHeader}
                        <EventCardLink {...linkProps}>
                            <EventCardTitle
                                className="event-card__clamp-line--two"
                                title={event.name}
                                color="neutral-800"
                            />
                        </EventCardLink>
                        {promotedLabelAsSubtitle}
                        <EventCardDate
                            className="event-card__clamp-line--one"
                            formattedDateString={formattedDateString}
                            variant="body-md"
                        />
                        <EventCardLocation
                            className="event-card__clamp-line--one"
                            formattedLocationString={formattedLocationString}
                        />
                        <EventCardDetailsSpacer />
                        <div className="discover-horizontal-event-card__price-wrapper">
                            <EventCardPrice
                                isFree={event.isFree}
                                formattedPriceString={formattedPriceString}
                                variant="body-md-bold"
                                color="neutral-800"
                                isLoading={isExpansionsLoading}
                            />
                            <EventCardPromoCode
                                openDiscount={event.openDiscount}
                                locale={locale}
                            />
                        </div>

                        {promotedLabelAsBottom}

                        <EventCardActions
                            tokens={{
                                '--EventCardActionsMargin': '12px 0 0 0',
                            }}
                        >
                            <EventCardShareAction
                                {...getShareActionProps({
                                    name: event.name,
                                    id: event.id,
                                    url: event.url,
                                    affCode: affCode,
                                })}
                            />
                            <EventCardSaveAction
                                isAuthenticated={!!props.isAuthenticated}
                                savedByYou={event.savedByYou}
                                id={event.id}
                                eventName={event.name}
                                imageUrl={event.imageUrl}
                            />
                            {moreActions}
                        </EventCardActions>
                    </EventCardDetails>
                </EventCardTracking>
            </HorizontalEventCard>
        </section>
    );
};
